import { QueryKey, useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';
import { ScheduledSemenItem } from '@/services/semen-pull';

import { UpsertSireNoteRequest } from './types';

export type { UpsertSireNoteRequest } from './types';

type SireNoteMutationContext = {
  previousScheduledQueries: [
    QueryKey,
    readonly ScheduledSemenItem[] | undefined,
  ][];
};

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function upsertSireNote(request: UpsertSireNoteRequest): Promise<void> {
  const response = await fetch(`${getBaseUrl()}/api/v1/sire-note`, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return;
}

export function useUpsertSireNoteMutation() {
  return useMutation({
    mutationFn: (request: UpsertSireNoteRequest) => upsertSireNote(request),
    async onMutate(request, { client }): Promise<SireNoteMutationContext> {
      await client.cancelQueries({
        queryKey: ['semen-pull', 'scheduled'],
      });

      const previousScheduledQueries = client.getQueriesData<
        readonly ScheduledSemenItem[]
      >({ queryKey: ['semen-pull', 'scheduled'] });

      previousScheduledQueries.forEach(([queryKey, scheduledSemen]) => {
        if (!scheduledSemen) {
          return;
        }

        client.setQueryData<readonly ScheduledSemenItem[]>(
          queryKey,
          (current) =>
            current?.map((row) => {
              if (
                row.sireId === request.sireId &&
                row.accountId === request.accountId &&
                row.donorScheduleId === request.donorScheduleId &&
                row.semenId === request.semenId &&
                row.colCode === request.collectionCode &&
                row.semenType === request.semenType
              ) {
                return { ...row, sireNote: request.note };
              }

              return row;
            }),
        );
      });

      return { previousScheduledQueries };
    },
    onError(_, __, context, { client }) {
      context?.previousScheduledQueries.forEach(
        ([queryKey, scheduledSemen]) => {
          client.setQueryData(queryKey, scheduledSemen);
        },
      );
    },
    async onSettled(_, _error, _variables, _ctx, { client }) {
      await client.invalidateQueries({
        queryKey: ['semen-pull', 'scheduled'],
      });
    },
  });
}
